import { merge, clone } from 'utilities/obj.js';
import { dynamicImport } from 'utilities/dynamicImport.ts';
import { Wistia } from '../../wistia_namespace.ts';

const languages = (Wistia.languages = Wistia.languages || {});
const translations = (Wistia.translations = Wistia.translations || {});

// set translationPromises if doesn't exist
if (!Wistia._translationPromises) {
  Wistia._translationPromises = {};
}

export const supportedLanguages = [
  'ar',
  'de',
  'es',
  'en-US',
  'fr',
  'it',
  'ja',
  'ko',
  'pt',
  'ru',
  'zh-CN',
];

const ietfLanguageTagToCode = {
  ara: 'ar',
  ger: 'de',
  spa: 'es',
  eng: 'en-US',
  fre: 'fr',
  ita: 'it',
  jpn: 'ja',
  kor: 'ko',
  por: 'pt',
  rus: 'ru',
  chi: 'zh-CN',
};

const normalizeLanguageCode = (code) => {
  if (code == null) {
    // whoops, passed no code, don't fail.
    return 'en-US';
  }

  // eng -> en-US, etc.
  code = ietfLanguageTagToCode[code] || code;

  // en-UK -> en, es-419 -> es, etc.
  if (!supportedLanguages.includes(code)) {
    const match = matchingLanguages(() => [code])[0];
    if (match) {
      code = match;
    }
  }

  // we have a translation for en-US, but not en
  if (code === 'en' || /^en-/.test(code)) {
    code = 'en-US';
  }

  // we have a translation for zh-CN, but not zh
  if (code === 'zh' || /^zh-/.test(code)) {
    code = 'zh-CN';
  }

  return code;
};

const defineLanguage = (code, text, translations) => {
  languages[code] = { code, text: decodeEntities(text) };
  if (translations) {
    defineTranslations(code, translations);
  }
};

export const getLanguage = (code) => {
  return languages[normalizeLanguageCode(code)];
};

export const isSupportedPlayerLanguage = (code) => {
  return supportedLanguages.includes(normalizeLanguageCode(code));
};

export const defineTranslations = (code, keyValPairs) => {
  if (languages[code] == null) {
    throw new Error(`Must define a language with code ${code} before defining its translations.`);
  }

  const translation = translations[code];
  if (translation) {
    merge(translation, keyValPairs);
  } else {
    translations[code] = clone(keyValPairs);
  }
};

const withFallbackText = (text) => {
  if (text == null) {
    return '?';
  }
  return text;
};

let dummyTextArea;
const cachedDecodings = (Wistia.cachedDecodings = Wistia.cachedDecodings || {});
const decodeEntities = (text) => {
  if (!dummyTextArea) {
    // Create this lazily to avoid race conditions while document is rendering.
    dummyTextArea = document.createElement('textarea');
  }
  if (cachedDecodings[text] != null) {
    // If we've decoded this before, use that value; DOM operations aren't
    // fast.
    return cachedDecodings[text];
  }
  dummyTextArea.innerHTML = text;
  cachedDecodings[text] = dummyTextArea.value;
  return dummyTextArea.value;
};

export const getTranslation = (code, key) => {
  code = normalizeLanguageCode(code);

  let text;
  if (translations[code] && translations[code][key]) {
    text = translations[code][key];
  } else {
    text = translations['en-US'][key];
  }
  return decodeEntities(withFallbackText(text));
};

/**
 * Gets translations for the specified language code and keys
 * @param {string} code - The language code to get translations for
 * @param {string[]} [keys=[]] - Array of translation keys to retrieve
 * @returns {Record<string, string>} Object containing the translations with keys as properties
 */
export const getTranslations = (code, keys = []) => {
  const translations = {};
  keys.forEach((key) => {
    translations[key] = getTranslation(code, key);
  });
  return translations;
};

export const getLanguagePreference = () => {
  if (navigator.languages || navigator.language) {
    Wistia.languagePreference = navigator.languages || [navigator.language];
  } else {
    // in old browsers that don't support navigator.language(s), like IE 9/10,
    // just go with English.
    Wistia.languagePreference = ['en-US'];
  }

  return Wistia.languagePreference;
};

let defaultPromise = Promise.resolve({
  code: 'en-US',
  translations: translations['en-US'],
});

export const loadAndDefineTranslation = (code) => {
  code = normalizeLanguageCode(code);

  if (!supportedLanguages.includes(code)) {
    // we don't support this language; just use en-US
    return defaultPromise;
  }

  if (Wistia._translationPromises[code]) {
    return Wistia._translationPromises[code];
  }

  if (code === 'en-US') {
    return defaultPromise;
  }

  const result = new Promise((resolve, reject) => {
    dynamicImport([`assets/external/translations/${code}.js`])
      .then((moduleClass) => {
        const { languageCode, languageLabel, translations } = moduleClass;
        defineLanguage(languageCode, languageLabel, translations);
        resolve({
          code,
          translations: translations[code],
        });
      })
      .catch(reject);
  });

  Wistia._translationPromises[code] = result;
  return result;
};

export const matchingLanguages = (languagePrefs = getLanguagePreference) => {
  return languagePrefs().reduce((matches, langCode) => {
    const isFullMatch = supportedLanguages.indexOf(langCode) !== -1;

    // strip the region off a language, e.g. `es-ES` -> `es`
    const partialLangCode = langCode.split('-')[0];

    // take a partial match if the language hits, just not the region, e.g. `es-ES` -> `es`
    const isPartialMatch = supportedLanguages.indexOf(partialLangCode) !== -1;

    if (isFullMatch) {
      matches.push(langCode);
    } else if (isPartialMatch) {
      matches.push(partialLangCode);
    }

    return matches;
  }, []);
};

export const getDefaultTranslation = () => {
  let matchingLanguage = matchingLanguages()[0];

  // if there is not match, or if it is 'en', go to our
  // default of 'en-US'
  if (!matchingLanguage || matchingLanguage === 'en') {
    matchingLanguage = 'en-US';
  }

  if (translations[matchingLanguage]) {
    return Promise.resolve({
      code: matchingLanguage,
      translations: translations[matchingLanguage],
    });
  }

  return loadAndDefineTranslation(matchingLanguage);
};

// Most translations are defined where they are used, however these translations are
// rather generic and used in multiple places. They are defined here so we don't
// duplicate multiple translation keys.
defineLanguage('en-US', 'English');
defineTranslations('en-US', {
  PAUSE: 'Pause',
  PLAY: 'Play',
  PLAY_BUTTON_LIVE_NOT_STARTED: 'Livestream has not started',
  PLAY_BUTTON_TITLE_WHEN_NOT_PLAYING: 'Play Video',
  PLAY_BUTTON_TITLE_WHEN_PLAYING: 'Pause Video',

  REWATCH: 'Rewatch',

  SKIP: 'Skip',
});
